import React, { useState, useEffect, useRef } from 'react';
import { 
  Vote, Check, X, Minus, Clock, AlertCircle, 
  FileText, Upload, Send, Edit2, CheckCircle,
  ChevronDown, ChevronUp, Download, Camera,
  Users, PenTool, RefreshCw, Eye
} from 'lucide-react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Vote button component
const VoteButton = ({ type, selected, onClick, disabled }) => {
  const configs = {
    pour: { 
      icon: Check, 
      label: 'Pour', 
      bgSelected: 'bg-green-500', 
      bgHover: 'hover:bg-green-100',
      textSelected: 'text-white',
      borderSelected: 'border-green-500'
    },
    contre: { 
      icon: X, 
      label: 'Contre', 
      bgSelected: 'bg-red-500', 
      bgHover: 'hover:bg-red-100',
      textSelected: 'text-white',
      borderSelected: 'border-red-500'
    },
    abstention: { 
      icon: Minus, 
      label: 'Abstention', 
      bgSelected: 'bg-gray-500', 
      bgHover: 'hover:bg-gray-100',
      textSelected: 'text-white',
      borderSelected: 'border-gray-500'
    }
  };
  
  const config = configs[type];
  const Icon = config.icon;
  
  return (
    <button
      onClick={() => onClick(type)}
      disabled={disabled}
      className={`
        flex items-center justify-center space-x-1 px-3 py-2 rounded-lg border-2 transition-all text-sm font-medium
        ${selected 
          ? `${config.bgSelected} ${config.textSelected} ${config.borderSelected}` 
          : `border-gray-200 text-gray-600 ${config.bgHover}`
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      data-testid={`vpc-vote-${type}`}
    >
      <Icon size={16} />
      <span className="hidden sm:inline">{config.label}</span>
    </button>
  );
};

// Main VPC Component for Copropriétaire
const VoteParCorrespondance = ({ 
  assemblee, 
  user, 
  token, 
  onVoteSubmitted 
}) => {
  const [config, setConfig] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [votes, setVotes] = useState({});
  const [vpcStatus, setVpcStatus] = useState('none'); // none, draft, signed
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedRes, setExpandedRes] = useState(null);
  const [participationMode, setParticipationMode] = useState(null);
  const [participationChecked, setParticipationChecked] = useState(false);
  
  const sigCanvas = useRef(null);
  
  // Check participation mode first
  useEffect(() => {
    if (!assemblee?.id || !token) return;
    
    const checkParticipation = async () => {
      try {
        const response = await axios.get(
          `${API}/assemblees/${assemblee.id}/my-participation`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setParticipationMode(response.data.mode || null);
        setParticipationChecked(true);
      } catch (err) {
        console.error('Error checking participation:', err);
        setParticipationMode(null);
        setParticipationChecked(true);
      }
    };
    
    checkParticipation();
  }, [assemblee?.id, token]);
  
  // Load VPC config and resolutions (only if mode is correspondance)
  useEffect(() => {
    if (!assemblee?.id || !token || !participationChecked) return;
    if (participationMode && participationMode !== 'correspondance') {
      setLoading(false);
      return;
    }
    
    const loadVPCData = async () => {
      setLoading(true);
      try {
        // Load config
        const configRes = await axios.get(
          `${API}/assemblees/${assemblee.id}/vpc/config`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setConfig(configRes.data);
        
        // Load resolutions
        const resRes = await axios.get(
          `${API}/assemblees/${assemblee.id}/vpc/resolutions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResolutions(resRes.data.resolutions || []);
        
        // Load existing votes
        const votesRes = await axios.get(
          `${API}/assemblees/${assemblee.id}/vpc/my-votes`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVotes(votesRes.data.votes || {});
        setVpcStatus(votesRes.data.status || 'none');
        
      } catch (err) {
        console.error('Error loading VPC data:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    
    loadVPCData();
  }, [assemblee?.id, token, participationMode, participationChecked]);
  
  // Handle vote change
  const handleVote = (resolutionId, voteType) => {
    if (vpcStatus === 'signed') return;
    
    setVotes(prev => ({
      ...prev,
      [resolutionId]: voteType
    }));
    setError(null);
  };
  
  // Vote all in one direction
  const handleVoteAll = (voteType) => {
    if (vpcStatus === 'signed') return;
    
    const newVotes = {};
    resolutions.forEach(res => {
      newVotes[res.id] = voteType;
    });
    setVotes(newVotes);
    setError(null);
  };
  
  // Save votes (draft)
  const handleSaveVotes = async () => {
    if (Object.keys(votes).length === 0) {
      setError('Veuillez voter sur au moins une résolution');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await axios.post(
        `${API}/assemblees/${assemblee.id}/vpc/vote`,
        { votes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVpcStatus('draft');
      setSuccess('Votes enregistrés. N\'oubliez pas de signer pour valider définitivement.');
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };
  
  // Sign votes
  const handleSign = async () => {
    // Check all resolutions are voted
    const missingVotes = resolutions.filter(r => !votes[r.id]);
    if (missingVotes.length > 0) {
      setError(`Vous devez voter sur toutes les résolutions. ${missingVotes.length} vote(s) manquant(s).`);
      return;
    }
    
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setError('Veuillez signer dans le cadre prévu');
      return;
    }
    
    setSigning(true);
    setError(null);
    
    try {
      // First save the votes
      await axios.post(
        `${API}/assemblees/${assemblee.id}/vpc/vote`,
        { votes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Then sign
      const signatureData = sigCanvas.current.toDataURL('image/png');
      
      const res = await axios.post(
        `${API}/assemblees/${assemblee.id}/vpc/sign`,
        { signature: signatureData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVpcStatus('signed');
      setShowSignature(false);
      setSuccess('Vote par correspondance signé avec succès ! Votre bulletin est enregistré.');
      
      if (onVoteSubmitted) {
        onVoteSubmitted(res.data);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };
  
  // Download PDF
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/assemblees/${assemblee.id}/vpc/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vpc_${assemblee.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      setError('Erreur lors du téléchargement du PDF');
    }
  };
  
  // Clear signature
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };
  
  // Calculate progress
  const votedCount = Object.keys(votes).length;
  const totalCount = resolutions.length;
  const progress = totalCount > 0 ? Math.round((votedCount / totalCount) * 100) : 0;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="animate-spin text-[#00D3A8] mr-2" size={24} />
        <span className="text-gray-600">Chargement...</span>
      </div>
    );
  }
  
  // Check if user has chosen a different participation mode
  if (participationChecked && participationMode && participationMode !== 'correspondance') {
    const modeLabels = {
      'video': 'Vidéoconférence',
      'audio': 'Audioconférence',
      'presentiel': 'Présentiel (en salle)',
      'delegation': 'Délégation de pouvoir'
    };
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="vpc-not-available">
        <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-4 text-white">
          <div className="flex items-center space-x-3">
            <Vote size={24} />
            <div>
              <h3 className="font-bold text-lg">Vote par Correspondance</h3>
              <p className="text-white/80 text-sm">Non disponible</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-gray-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Vote par correspondance non accessible
          </h3>
          <p className="text-gray-600 mb-4">
            Vous avez choisi de participer à cette assemblée en mode <strong className="text-[#00D3A8]">{modeLabels[participationMode] || participationMode}</strong>.
          </p>
          <p className="text-gray-500 text-sm">
            Le vote par correspondance est réservé aux coproprietaires ayant choisi ce mode de participation.
          </p>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-blue-700 text-sm">
              <strong>Note :</strong> Vous pourrez voter lors de l'assemblée générale via votre mode de participation choisi 
              ({modeLabels[participationMode] || participationMode}).
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // If no participation mode confirmed yet, show a message
  if (participationChecked && !participationMode) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="vpc-no-participation">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
          <div className="flex items-center space-x-3">
            <Vote size={24} />
            <div>
              <h3 className="font-bold text-lg">Vote par Correspondance</h3>
              <p className="text-white/80 text-sm">Confirmation requise</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-amber-500" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Confirmez votre mode de participation
          </h3>
          <p className="text-gray-600 mb-4">
            Pour accéder au vote par correspondance, vous devez d'abord confirmer votre participation 
            en choisissant le mode <strong>"Vote par correspondance"</strong>.
          </p>
          <p className="text-gray-500 text-sm">
            Rendez-vous dans l'onglet <strong>Informations</strong> ou <strong>Salle de réunion</strong> 
            pour confirmer votre mode de participation.
          </p>
        </div>
      </div>
    );
  }
  
  if (!config?.enabled) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
        <AlertCircle className="mx-auto text-yellow-500 mb-2" size={32} />
        <p className="text-yellow-700">Le vote par correspondance n'est pas activé pour cette AG.</p>
      </div>
    );
  }
  
  const isOpen = config?.is_open;
  const isSigned = vpcStatus === 'signed';
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="vpc-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Vote size={24} />
            <div>
              <h3 className="font-bold text-lg">Vote par Correspondance</h3>
              <p className="text-white/80 text-sm">
                {isSigned ? 'Votre vote a été enregistré' : 'Votez avant la date limite'}
              </p>
            </div>
          </div>
          {config?.deadline && (
            <div className="text-right">
              <div className="flex items-center space-x-1 text-white/80 text-sm">
                <Clock size={14} />
                <span>Date limite :</span>
              </div>
              <p className="font-semibold">
                {new Date(config.deadline).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Status banner */}
      {isSigned && (
        <div className="bg-green-50 border-b border-green-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle size={20} />
            <span className="font-medium">Vote signé et enregistré</span>
          </div>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
          >
            <Download size={16} />
            <span>Télécharger PDF</span>
          </button>
        </div>
      )}
      
      {!isOpen && !isSigned && (
        <div className="bg-red-50 border-b border-red-200 p-4">
          <div className="flex items-center space-x-2 text-red-700">
            <AlertCircle size={20} />
            <span className="font-medium">Le délai de vote par correspondance est dépassé.</span>
          </div>
        </div>
      )}
      
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 p-3 flex items-center space-x-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border-b border-green-200 p-3 flex items-center space-x-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}
      
      {/* Quick vote buttons */}
      {isOpen && !isSigned && (
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-gray-600 mb-1">Vote rapide sur toutes les résolutions :</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleVoteAll('pour')}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center space-x-1"
                >
                  <Check size={16} />
                  <span>Tout POUR</span>
                </button>
                <button
                  onClick={() => handleVoteAll('contre')}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center space-x-1"
                >
                  <X size={16} />
                  <span>Tout CONTRE</span>
                </button>
                <button
                  onClick={() => handleVoteAll('abstention')}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 flex items-center space-x-1"
                >
                  <Minus size={16} />
                  <span>Tout ABSTENTION</span>
                </button>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Progression</p>
              <p className="text-2xl font-bold text-[#00D3A8]">{progress}%</p>
              <p className="text-xs text-gray-500">{votedCount}/{totalCount} résolutions</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Resolutions list */}
      <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
        {resolutions.map((resolution, index) => (
          <div 
            key={resolution.id} 
            className={`p-4 ${votes[resolution.id] ? 'bg-white' : 'bg-yellow-50/50'}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-semibold text-gray-400">#{index + 1}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    resolution.majorite === 'art24' ? 'bg-blue-100 text-blue-700' :
                    resolution.majorite === 'art25' ? 'bg-orange-100 text-orange-700' :
                    resolution.majorite === 'art26' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {resolution.majorite?.toUpperCase() || 'Art.24'}
                  </span>
                </div>
                <h4 className="font-medium text-gray-800 text-sm mb-1">{resolution.titre}</h4>
                {expandedRes === resolution.id && resolution.description && (
                  <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                    {resolution.description}
                  </p>
                )}
                {resolution.description && (
                  <button
                    onClick={() => setExpandedRes(expandedRes === resolution.id ? null : resolution.id)}
                    className="text-xs text-[#00D3A8] hover:underline mt-1"
                  >
                    {expandedRes === resolution.id ? 'Masquer détails' : 'Voir détails'}
                  </button>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <VoteButton
                  type="pour"
                  selected={votes[resolution.id] === 'pour'}
                  onClick={(type) => handleVote(resolution.id, type)}
                  disabled={!isOpen || isSigned}
                />
                <VoteButton
                  type="contre"
                  selected={votes[resolution.id] === 'contre'}
                  onClick={(type) => handleVote(resolution.id, type)}
                  disabled={!isOpen || isSigned}
                />
                <VoteButton
                  type="abstention"
                  selected={votes[resolution.id] === 'abstention'}
                  onClick={(type) => handleVote(resolution.id, type)}
                  disabled={!isOpen || isSigned}
                />
              </div>
            </div>
          </div>
        ))}
        
        {resolutions.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <FileText className="mx-auto mb-2 text-gray-300" size={32} />
            <p>Aucune résolution disponible</p>
          </div>
        )}
      </div>
      
      {/* Actions */}
      {isOpen && !isSigned && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          {!showSignature ? (
            <div className="flex items-center justify-between">
              <button
                onClick={handleSaveVotes}
                disabled={saving || Object.keys(votes).length === 0}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
              </button>
              
              <button
                onClick={() => setShowSignature(true)}
                disabled={votedCount !== totalCount}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg font-medium hover:bg-[#00C7A1] disabled:opacity-50 flex items-center space-x-2"
              >
                <PenTool size={18} />
                <span>Signer et valider</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Signez ci-dessous pour valider définitivement votre vote :
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: 'w-full h-32 rounded-lg',
                      style: { width: '100%', height: '128px' }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <button
                    onClick={clearSignature}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Effacer la signature
                  </button>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Attention :</strong> Une fois signé, votre vote sera définitif. 
                  Seul le gestionnaire pourra le modifier en cas de besoin.
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowSignature(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {signing ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      <span>Signature en cours...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      <span>Confirmer et signer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoteParCorrespondance;
